/**
 * VideoStructuredData — JSON-LD `VideoObject` para que Google Video indexe los
 * videos del sitio.
 *
 * `StructuredData.tsx` ya emite Person/WebSite/WebPage/BreadcrumbList, pero no
 * emitía ningún VideoObject: por eso los videos eran invisibles para Google
 * Video / la pestaña Videos y para los rich results de video.
 *
 * Requisito DURO de Google (developers.google.com/search/docs/appearance/structured-data/video):
 * `name`, `description`, `thumbnailUrl` y `uploadDate` son OBLIGATORIOS. Si
 * falta alguno el componente devuelve `null` — mejor no emitir nada que emitir
 * schema inválido (Search Console lo marca como error y no gana nada).
 *
 * Las URLs de thumbnail siguen el mismo patrón que usa el resto del repo para
 * Mux Image (ver `MuxPosterBackdrop.tsx`): `image.mux.com/<pid>/thumbnail.*`.
 */

import { useLocation } from "react-router-dom";
import {
  BRAND_PERSON_NAME,
  BRAND_SITE_URL,
  BRAND_LANGUAGE,
} from "@/lib/brandConfig";
import { getVideoSeoEntryForPath } from "@/config/videoSeoConfig";

export interface VideoStructuredDataProps {
  /** Mux playback ID (público). */
  playbackId: string;
  /** schema.org `name` — obligatorio. */
  title: string;
  /** schema.org `description` — obligatorio. */
  description: string;
  /** ISO 8601 — obligatorio para rich results. */
  uploadDate?: string;
  durationSeconds?: number;
  /** Transcript opcional (mejora la comprensión del contenido). */
  transcript?: string;
  /** URL canónica de la página que contiene el video. Default: la actual. */
  pageUrl?: string;
}

/** Thumbnail de Mux. Mismo host/patrón que MuxPosterBackdrop. */
export function muxThumbnailUrl(playbackId: string, width = 1200): string {
  return `https://image.mux.com/${playbackId}/thumbnail.jpg?width=${width}&fit_mode=preserve`;
}

/** Segundos → duración ISO 8601 (`PT#M#S`). */
export function toIso8601Duration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `PT${h > 0 ? `${h}H` : ""}${m > 0 ? `${m}M` : ""}${s > 0 || (h === 0 && m === 0) ? `${s}S` : ""}`;
}

export const VideoStructuredData = ({
  playbackId,
  title,
  description,
  uploadDate,
  durationSeconds,
  transcript,
  pageUrl,
}: VideoStructuredDataProps) => {
  // Campos obligatorios de Google. Sin ellos → no emitir nada.
  if (!playbackId || !title?.trim() || !description?.trim() || !uploadDate?.trim()) {
    return null;
  }

  const thumbnailUrl = muxThumbnailUrl(playbackId);
  const creator = {
    "@type": "Person",
    name: BRAND_PERSON_NAME,
    url: BRAND_SITE_URL,
  };

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title.trim(),
    description: description.trim(),
    thumbnailUrl: [thumbnailUrl],
    uploadDate,
    // HLS master del asset — es el contenido real reproducible.
    contentUrl: `https://stream.mux.com/${playbackId}.m3u8`,
    // Mux no expone un player embebible propio: la página del sitio ES el
    // reproductor, así que embedUrl apunta a la URL canónica de la página.
    embedUrl: pageUrl || BRAND_SITE_URL,
    inLanguage: BRAND_LANGUAGE,
    isFamilyFriendly: true,
    creator,
    publisher: creator,
  };

  if (typeof durationSeconds === "number" && durationSeconds > 0) {
    schema.duration = toIso8601Duration(durationSeconds);
  }
  if (transcript?.trim()) {
    schema.transcript = transcript.trim();
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

/**
 * Monta el VideoObject de la ruta actual, si y sólo si esa ruta está declarada
 * como indexable en `videoSeoConfig.ts`. Se monta una sola vez, junto a
 * `<StructuredData />` en App.tsx — así ninguna ruta noindex (/desnuda, /nude,
 * /videos, /reels, /premium…) recibe schema de video.
 */
export const RouteVideoStructuredData = () => {
  const location = useLocation();
  const entry = getVideoSeoEntryForPath(location.pathname);
  if (!entry) return null;

  return (
    <VideoStructuredData
      playbackId={entry.playbackId}
      title={entry.title}
      description={entry.description}
      uploadDate={entry.uploadDate}
      durationSeconds={entry.durationSeconds}
      pageUrl={`${BRAND_SITE_URL}${entry.path === "/" ? "" : entry.path}`}
    />
  );
};

export default VideoStructuredData;
